/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { heartbeat, signOut } from '@/api/auth';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { type ActivityBus, activityBus } from '@/lib/activity';
import { markTimedOut } from '@/lib/session';

/** How often a person's activity is passed on to the server, at most. */
const BEAT_EVERY_MS = 60_000;

/**
 * How often it is passed on to the other tabs, at most. Every keystroke would
 * be a message for nothing: the idle window is minutes long, and a clock a
 * few seconds behind asks its question a few seconds late.
 */
const SHARE_EVERY_MS = 5_000;

/**
 * Signing somebody out who has walked away, with a warning first (`AUTH-6`).
 *
 * The rule the warning exists for: being signed out of a half-finished form
 * with no notice is worse than the risk the timeout guards against. So the
 * session ends in two steps — a question, and then, if nobody answers it, the
 * sign-out.
 *
 * Idleness is measured from what a person does, not from what the application
 * does. A background refetch is not somebody sitting at the desk, and counting
 * it would make the timeout a fiction. Once the question is on screen, moving
 * the mouse is not an answer either: only the button is, because the whole
 * point is to find out whether anybody is there.
 *
 * Both numbers come from the server (`/me`), so one setting moves the window
 * and the notice together and the interface never disagrees with the session
 * it is describing.
 *
 * Every open tab shares one clock (`lib/activity`). The session is one cookie
 * for all of them, so a tab left in the background must not decide somebody
 * has gone while they type in another: activity anywhere resets the clock
 * everywhere, and puts away a question another tab was asking. A sign-out in
 * one tab sends the rest to the sign-in screen at once.
 */
export function SessionGuard({
  idleMinutes,
  warningSeconds,
  bus = activityBus,
}: {
  idleMinutes: number;
  warningSeconds: number;
  /** How the tabs talk to each other; a test passes its own. */
  bus?: ActivityBus;
}) {
  const client = useQueryClient();
  const navigate = useNavigate();

  const [asking, setAsking] = useState(false);
  const [left, setLeft] = useState(warningSeconds);
  const [going, setGoing] = useState(false);

  // Held in a ref rather than state: every mouse move would otherwise render
  // the whole application. Zero until mounted, because reading the clock while
  // rendering is not something a component may do.
  const lastActive = useRef(0);
  const asked = useRef(false);
  // When the server was last told somebody is here. Its idle clock moves only
  // on requests, and a long form typed without saving makes none: without this
  // the browser saw somebody working while the server let the session lapse,
  // and the next save signed them out mid-sentence.
  const lastBeat = useRef(0);
  // When the other tabs were last told. Zero, so the first act is shared.
  const lastShared = useRef(0);
  // Set once leaving has begun. The timer and the button can both arrive at
  // `leave`, and a double click arrives twice; one sign-out is enough.
  const leaving = useRef(false);

  // How far the sign-out reaches depends on who asked for it. "Sign out now"
  // is somebody choosing to leave, so it ends the identity provider's session
  // too, as the rail's Sign out does (AUTHN-8). The countdown running out is
  // nobody choosing anything: it ends Vellum's session only. Ending the
  // provider's would sign the person out of every Microsoft app in the
  // browser — mail, Teams — because one Vellum tab sat untouched, and it would
  // trade the sign-in screen's "you were signed out for being idle" for the
  // provider's own page.
  const leave = useCallback(
    async (chosen: boolean) => {
      if (leaving.current) return;
      leaving.current = true;
      setGoing(true);
      try {
        // On the way to the provider: it brings the browser back to sign-in.
        if (await signOut(client, { provider: chosen })) return;
      } catch {
        // The server did not confirm it, but nobody is here to tell: this runs
        // on a timer, or from a button whose only promise is to leave. The
        // cache is already cleared, and the next sign-in starts a session
        // regardless.
      }
      // Whatever the server said, the browser is done with this session. The
      // reason travels with it so the sign-in screen can explain itself rather
      // than looking like an unexplained ejection.
      markTimedOut();
      await navigate({ to: '/signin' });
    },
    [client, navigate],
  );

  const stay = useCallback(() => {
    const now = Date.now();
    lastActive.current = now;
    asked.current = false;
    setAsking(false);
    setLeft(warningSeconds);
    // An answer is the clearest sign anybody gives; the other tabs put their
    // own question away on hearing it.
    lastShared.current = now;
    bus.announceActive(now);
    void heartbeat().catch(() => {
      // A failed heartbeat is not worth interrupting anybody for. The next
      // request will find out whether the session really ended.
    });
  }, [warningSeconds, bus]);

  // What the other tabs say. Activity there is somebody here, so this tab's
  // clock moves with it -- and a question on screen is put away, because the
  // rule that only the button answers it is about this tab's own events. A
  // keystroke in another tab is a person, not a stray pointer. A sign-out
  // there ended this session too, so the screen goes now, with its data.
  useEffect(
    () =>
      bus.listen((message) => {
        if (message.kind === 'active') {
          lastActive.current = Math.max(lastActive.current, message.at);
          if (asked.current) {
            asked.current = false;
            setAsking(false);
            setLeft(warningSeconds);
          }
          return;
        }
        if (leaving.current) return;
        leaving.current = true;
        client.clear();
        void navigate({ to: '/signin' });
      }),
    [bus, client, navigate, warningSeconds],
  );

  // What counts as being here. Deliberately coarse: a keystroke, a click, a
  // scroll, a touch. Not a network response, and not a mouse crossing the
  // window on its way somewhere else.
  useEffect(() => {
    // The clock starts when the frame mounts, which is when somebody arrived.
    lastActive.current = Date.now();
    lastBeat.current = Date.now();

    const mark = () => {
      if (asked.current) return;
      const now = Date.now();
      lastActive.current = now;
      if (now - lastShared.current >= SHARE_EVERY_MS) {
        lastShared.current = now;
        bus.announceActive(now);
      }
      // At most once a minute: the server extends its window no more often.
      if (now - lastBeat.current >= BEAT_EVERY_MS) {
        lastBeat.current = now;
        void heartbeat().catch(() => {
          // The next request finds out whether the session really ended.
        });
      }
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    for (const event of events) document.addEventListener(event, mark, { passive: true });
    return () => {
      for (const event of events) document.removeEventListener(event, mark);
    };
  }, [bus]);

  // One timer for both halves. A second's resolution is enough for a countdown
  // somebody reads, and cheap enough to leave running.
  useEffect(() => {
    const idleMs = idleMinutes * 60_000;

    const tick = () => {
      // Before the mount effect has run there is no clock to read from.
      if (lastActive.current === 0) return;
      const idleFor = Date.now() - lastActive.current;

      if (!asked.current) {
        if (idleFor >= idleMs) {
          asked.current = true;
          setLeft(warningSeconds);
          setAsking(true);
        }
        return;
      }

      const remaining = Math.ceil((idleMs + warningSeconds * 1000 - idleFor) / 1000);
      setLeft(Math.max(0, remaining));
      if (remaining <= 0) void leave(false);
    };

    const timer = setInterval(tick, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [idleMinutes, warningSeconds, leave]);

  if (!asking) return null;

  return (
    <Modal
      title="Are you still there?"
      onClose={stay}
      footer={
        <>
          <button type="button" className="btn btn-p" disabled={going} onClick={stay}>
            <Icon name="check" size={14} />
            Keep me signed in
          </button>
          <button
            type="button"
            className="btn btn-t"
            disabled={going}
            onClick={() => {
              void leave(true);
            }}
          >
            Sign out now
          </button>
        </>
      }
    >
      <p>
        You have not done anything for{' '}
        {idleMinutes === 1 ? 'a minute' : `${String(idleMinutes)} minutes`}. For safety,
        Vellum will sign you out in{' '}
        <b>
          {left} second{left === 1 ? '' : 's'}
        </b>
        .
      </p>
      <p className="mut mt-sm!">
        Anything you have typed but not saved will be lost. Choose{' '}
        <b>Keep me signed in</b> to carry on where you left off.
      </p>
    </Modal>
  );
}
